import CheckIcon from '@mui/icons-material/Check';
import {
  Alert,
  Badge,
  Box,
  Button,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import JiraIcon from '../../assets/icons/jira.svg';
import { getLinkedJiraProjects } from '../../modules/project';
import {
  jiraChangeWarning,
  jiraIntegrationBenefits,
} from '../../static/common';
import Modal from '../elements/Modal';
import ChangeSiteModal from './ChangeSiteModal';
import JiraProjectSelection from './JiraProjectSelection';
import SyncJiraIssuesModal from './SyncJiraIssuesModal';
import { getUserMappings } from '../../modules/jira';
import { useQuery } from '@tanstack/react-query';
import JiraUserMappingTableModal from './JiraUserMappingPage';

const PlatformIntegrationSection = ({ isAdmin, currentProject }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [linkedJiraProject, setLinkedJiraProject] = useState(null);
  const [showChangeSitePopup, setShowChangeSitePopup] = useState(false);
  const [showAddProjectModal, setShowAddProjectModal] = useState(false);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [showUserMappingModal, setShowUserMappingModal] = useState(false);

  const fetchConnectedProject = async () => {
    setIsLoading(true);
    try {
      const res = await getLinkedJiraProjects(currentProject?.id);
      setLinkedJiraProject(res?.projects);
    } catch (error) {
      console.log('Failed to fetch projects. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchConnectedProject();
  }, []);

  const handleProjectSubmit = () => {
    setShowAddProjectModal(false);
    fetchConnectedProject();
  };

  const { data: mappingsData } = useQuery({
    queryKey: ['jira-mappings', currentProject?.id],
    queryFn: () => getUserMappings(currentProject.id),
    enabled: !!currentProject?.id && isAdmin,
  });

  const unmappedCount = useMemo(() => {
    if (!mappingsData?.mapped || !mappingsData?.jira_users) return 0;

    const mappedAccountIds = new Set(
      mappingsData.mapped
        .map((m) => m.jira_account_id)
        .filter(Boolean)
    );

    return mappingsData.jira_users.filter(
      (j) =>
        j.jira_account_id &&
        !mappedAccountIds.has(j.jira_account_id)
    ).length;
  }, [mappingsData]);

  return (
    <>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 3 }}>
        <Box display="flex" alignItems="center">
          <img
            src={JiraIcon}
            alt="Jira Logo"
            style={{ width: 24, height: 24, marginRight: 8 }}
          />
          <Typography variant="h5" fontWeight="bold">
            JIRA
          </Typography>
        </Box>
        <Typography
          variant="body2"
          color="success.main"
          sx={{
            fontWeight: 'bold',
            border: '1px solid',
            borderRadius: 1,
            px: 2,
            py: 1,
            marginRight: 5,
          }}>
          Connected
        </Typography>
      </Box>
      <CardContent sx={{ p: 3 }}>
        <Typography variant="body1" gutterBottom>
          Manage your projects and tickets seamlessly by integrating with Jira.
        </Typography>
        <Typography variant="body1" gutterBottom>
          Integration Benefits:
        </Typography>
        <List sx={{ textAlign: 'left', pl: 2 }}>
          {jiraIntegrationBenefits.map((text) => (
            <ListItem key={text} sx={{ p: 1, pl: 0 }}>
              <ListItemIcon>
                <CheckIcon fontSize="small" color="primary" />
              </ListItemIcon>
              <Typography variant="body2">{text}</Typography>
            </ListItem>
          ))}
        </List>

        <Divider sx={{ mb: 2 }} />

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1">Connected Workspace:</Typography>
          <Typography variant="body2" color="text.secondary">
            {currentProject?.jira_site_url || 'No Jira site connected.'}
          </Typography>
          {isAdmin && (
            <Button sx={{ pl: 0 }} onClick={() => setShowChangeSitePopup(true)}>
              Change Site URL
            </Button>
          )}
        </Box>

        <Box>
          <Typography variant="subtitle1">Connected JIRA Project:</Typography>
          {isLoading ? (
            <Typography variant="body2" color="text.secondary">
              Fetching project details...
            </Typography>
          ) : (
            <div>
              {linkedJiraProject?.length > 0 ? (
                <>
                  {linkedJiraProject?.map((project, idx) => (
                    <Typography variant="body2" color="text.secondary" key={idx}>
                      {project.linked_project_key} -{' '}
                      {project.linked_project_name}
                    </Typography>
                  ))}
                </>
              ) : (
                'No Jira project connected.'
              )}
            </div>
          )}
          {isAdmin && (
            <Button sx={{ pl: 0 }} onClick={() => setShowAddProjectModal(true)}>
              Modify Projects
            </Button>
          )}
          {isAdmin && (
            <Box mt={2}>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="subtitle1">
                  Sync Jira Users
                </Typography>

                {unmappedCount > 0 && (
                  <Badge
                    color="warning"
                    badgeContent="Required"
                    sx={{
                      '& .MuiBadge-badge': {
                        position: 'static',
                        transform: 'none',
                        fontSize: 12,
                      },
                    }}
                  />
                )}
              </Box>

              <Typography variant="body2" color="text.secondary">
                Fetch the users from your connected Jira projects and map them
                to GAiNSS users. This ensures issues, tickets, and assignments
                stay aligned between Jira and GAiNSS.
              </Typography>

              <Button
                sx={{ pl: 0 }}
                onClick={() => setShowUserMappingModal(true)}
              >
                Sync Jira Users
              </Button>
            </Box>
          )}
          {isAdmin && (
            <Box mt={2}>
              <Typography variant="subtitle1">Sync Linked Jira Issues</Typography>
              <Typography variant="body2" color="text.secondary">
                If linked Jira issues have been moved to a different Jira project, sync
                them to update their references in GAiNSS.
              </Typography>
              <Button sx={{ pl: 0 }} onClick={() => setShowSyncModal(true)}>
                Sync Jira Issues
              </Button>
            </Box>
          )}
        </Box>
      </CardContent>

      <ChangeSiteModal
        show={showChangeSitePopup}
        handleClose={() => setShowChangeSitePopup(false)}
      />
      {showAddProjectModal && (
        <Modal
          open={showAddProjectModal}
          width="sm"
          handleClose={() => setShowAddProjectModal(false)}
          modalTitle="Modify Linked Projects">
          <Alert severity="warning" sx={{ mb: 2 }}>
            {jiraChangeWarning('project')}
          </Alert>
          <JiraProjectSelection
            handleClose={handleProjectSubmit}
            linkedProjects={linkedJiraProject}
            type="change"
            projectId={currentProject?.id}
          />
        </Modal>
      )}
      <SyncJiraIssuesModal
        currentProject={currentProject}
        setShowSyncModal={setShowSyncModal}
        showSyncModal={showSyncModal}
      />
      <JiraUserMappingTableModal project={currentProject} open={showUserMappingModal} handleClose={() => setShowUserMappingModal(false)} isAdmin={isAdmin} />
    </>
  );
};

export default PlatformIntegrationSection;
